import React, { useEffect, useMemo } from 'react';
import { Box, ButtonV2, Flex, TextV2, Accordion } from '@withjoy/joykit';
import { Close, CheckOutline, ArrowLeft, ArrowRight, Checkmark } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core/i18n/useTranslation';
import { useTour } from './context/TourContext';
import type { TourStepContentProps } from './Tour.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useTourTelemetry } from './Tour.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface PreviousButtonProps
  extends Readonly<{
    showPreviousButton: boolean;
    prevButtonText: string;
    handlePrevious: () => void;
  }> {}

/**
 * PreviousButton - Button for the previous step
 *
 * @param showPreviousButton - Whether to show the previous button
 * @param prevButtonText - Text for the previous button
 * @param handlePrevious - Function to handle the previous step
 * @returns React.ReactNode
 */
const PreviousButton: React.FC<PreviousButtonProps> = ({ showPreviousButton, prevButtonText, handlePrevious }) => {
  const isMobile = useIsMobileScreen();

  if (!showPreviousButton) return null;

  if (isMobile) {
    return (
      <ButtonV2
        flex={0}
        variant="ghost"
        intent="neutral"
        shape="rounded"
        paddingX={0}
        title={prevButtonText}
        aria-label={prevButtonText}
        minWidth={pxToRem(40)}
        height={pxToRem(40)}
        onClick={handlePrevious}
        data-tour-ui="true"
      >
        <ArrowLeft color="newUIBrandPrimary" />
      </ButtonV2>
    );
  }

  return (
    <ButtonV2
      flex={1}
      variant="ghost"
      intent="neutral"
      shape="rounded"
      paddingX={6}
      title={prevButtonText}
      aria-label={prevButtonText}
      minWidth="fit-content"
      height={pxToRem(48)}
      onClick={handlePrevious}
      data-tour-ui="true"
    >
      {prevButtonText}
    </ButtonV2>
  );
};

interface NextButtonProps
  extends Readonly<{
    showNextButton: boolean;
    nextButtonText: string;
    handleNext: () => void;
  }> {}

/**
 * NextButton - Button for the next step
 *
 * @param showPreviousButton - Whether to show the previous button
 * @param showNextButton - Whether to show the next button
 * @param nextButtonText - Text for the next button
 * @param handleNext - Function to handle the next step
 * @returns React.ReactNode
 */
const NextButton: React.FC<NextButtonProps> = ({ showNextButton, nextButtonText, handleNext }) => {
  const isMobile = useIsMobileScreen();

  if (isMobile) {
    return (
      <ButtonV2
        flex={0}
        variant="ghost"
        intent="neutral"
        shape="rounded"
        paddingX={0}
        title={nextButtonText}
        aria-label={nextButtonText}
        minWidth={pxToRem(40)}
        height={pxToRem(40)}
        onClick={handleNext}
        data-tour-ui="true"
      >
        {showNextButton ? <ArrowRight color="newUIBrandPrimary" /> : <Checkmark color="newUIBrandPrimary" />}
      </ButtonV2>
    );
  }

  return (
    <ButtonV2
      flex={1}
      variant="solid"
      intent="primary"
      shape="rounded"
      paddingX={6}
      title={nextButtonText}
      aria-label={nextButtonText}
      minWidth="fit-content"
      height={pxToRem(48)}
      onClick={handleNext}
      data-tour-ui="true"
    >
      {nextButtonText}
    </ButtonV2>
  );
};

interface BenefitsListProps
  extends Readonly<{
    benefits: ReadonlyArray<string>;
  }> {}

/**
 * BenefitsList - Displays a list of benefits with checkmarks.
 *
 * @param benefits - Array of benefits to display
 * @returns React.ReactNode
 */
const BenefitsList: React.FC<BenefitsListProps> = ({ benefits }) => {
  if (benefits.length === 0) return null;

  return (
    <Flex
      flexDirection="column"
      gap={4}
      marginBottom={6}
    >
      {benefits.filter(Boolean).map((benefit, index) => {
        if (!benefit) return null;
        return (
          <Flex
            key={index}
            gap={3}
            alignItems="center"
          >
            {benefits.length > 1 && (
              <Box flexShrink={0}>
                <CheckOutline size={24} />
              </Box>
            )}
            <TextV2
              typographyVariant={{ _: 'label2', md: 'body2' }}
              flex={1}
            >
              {benefit}
            </TextV2>
          </Flex>
        );
      })}
    </Flex>
  );
};

/**
 * CloseButton - Button for the close tour
 *
 * @param handleClose - Function to handle the close tour
 * @returns React.ReactNode
 */
const CloseButton: React.FC<{ handleClose: () => void }> = ({ handleClose }) => {
  const { tq } = useTranslation('tour');

  return (
    <Flex
      justifyContent="flex-end"
      position="absolute"
      top={5}
      right={4}
    >
      <ButtonV2
        variant="ghost"
        intent="neutral"
        onClick={handleClose}
        aria-label={tq`Close tour`}
        padding={0}
        width={pxToRem(16)}
        minWidth={pxToRem(16)}
        height={pxToRem(16)}
        data-tour-ui="true"
      >
        <Close size={16} />
      </ButtonV2>
    </Flex>
  );
};

interface AccordionItemProps
  extends Readonly<{
    title?: string;
    handleClose: () => void;
    isAccordionExpanded: boolean;
    renderContent: React.ReactNode;
  }> {}

const AccordionItemContent: React.FC<AccordionItemProps> = ({ isAccordionExpanded, title, handleClose, renderContent }) => {
  const { tq } = useTranslation('tour');
  return (
    <>
      <Accordion.Button
        paddingX={7}
        paddingTop={isAccordionExpanded ? 9 : 7}
        paddingBottom={isAccordionExpanded ? 5 : 7}
        height="auto"
        _hover={{ backgroundColor: 'transparent' }}
        _active={{ backgroundColor: 'transparent' }}
        data-tour-ui="true"
        position="relative"
      >
        <TextV2 typographyVariant="hed4">{title ?? tq`Why it’s our pick?`}</TextV2>
        <Accordion.Icon marginLeft="auto" />
        {isAccordionExpanded && <CloseButton handleClose={handleClose} />}
      </Accordion.Button>
      <Accordion.Panel
        position="relative"
        paddingX={7}
        paddingTop={0}
        paddingBottom={7}
      >
        {renderContent}
      </Accordion.Panel>
    </>
  );
};

/**
 * TourStepContent - Generic reusable template for tour step content
 *
 * Provides a standard layout with title, benefits list, image, and navigation controls.
 * This is a domain-agnostic component with no business logic.
 *
 * Navigation controls (onNext, onPrevious, onClose) can be provided via props or will be
 * automatically obtained from the tour context if available.
 */
const TourStepContentComponent: React.FC<TourStepContentProps> = ({
  title,
  benefits = [],
  renderPrice,
  image,
  prevButtonText: prevButtonTextProp,
  nextButtonText: nextButtonTextProp,
  isCollapsed: isCollapsedFromStep,
  children,
}) => {
  const tour = useTour();
  const { tq } = useTranslation('tour');
  const telemetry = useTourTelemetry();

  // Extract stable function references to avoid closures over changing stateProps object
  const { isCollapsed: isCollapsed, currentStepName, currentStepIndex, hasNext, hasPrevious, next, prev, stop, setIsCollapsed } = tour.state.stateProps;

  useEffect(() => {
    if (!isCollapsedFromStep && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isCollapsedFromStep, isCollapsed, setIsCollapsed]);

  const handleAccordionChange = useEventCallback((idx: number) => {
    const isCollapsed = idx === 0;
    setIsCollapsed(!isCollapsed);
    telemetry.tourStepCompactViewToggled(!isCollapsed, tour.tourRegistry.tourName, currentStepName, currentStepIndex + 1);
  });

  const prevButtonText = prevButtonTextProp || tq`Previous`;
  const nextButtonText = nextButtonTextProp || (hasNext ? tq`Next` : tq`Done`);

  const renderContent = useMemo(() => {
    return (
      <>
        {children}

        {/* Image */}
        {image && (
          <Box
            marginBottom={5}
            borderRadius={12}
            overflow="hidden"
          >
            <Box
              as="img"
              src={image.src}
              alt={image.alt}
              width="100%"
              height={180}
              objectFit="cover"
            />
          </Box>
        )}

        {/* Title */}
        {!isCollapsedFromStep && title && (
          <TextV2
            typographyVariant="hed3"
            marginBottom={5}
          >
            {title}
          </TextV2>
        )}

        {/* Benefits list */}
        <BenefitsList benefits={benefits} />

        {/* Price */}
        {renderPrice}

        {/* Action buttons */}
        <Flex
          marginTop={6}
          flexWrap="wrap-reverse"
          justifyContent={{
            _: !hasPrevious ? 'flex-end' : !hasNext ? 'flex-start' : 'space-between',
            md: 'space-between',
          }}
          alignItems="center"
          gap={4}
        >
          <PreviousButton
            showPreviousButton={hasPrevious}
            prevButtonText={prevButtonText}
            handlePrevious={prev}
          />
          <NextButton
            showNextButton={hasNext}
            nextButtonText={nextButtonText}
            handleNext={next}
          />
        </Flex>
      </>
    );
  }, [children, image, title, benefits, renderPrice, hasNext, hasPrevious, prevButtonText, nextButtonText, next, prev, isCollapsedFromStep]);

  return (
    <Box
      position="relative"
      width={{ _: pxToRem(339), sm2: pxToRem(397) }}
      maxWidth="100%"
    >
      {isCollapsedFromStep ? (
        <Accordion
          index={isCollapsed ? -1 : 0}
          onChange={handleAccordionChange}
        >
          <Accordion.Item border="none">
            {({ isExpanded: isAccordionExpanded }) => (
              <AccordionItemContent
                isAccordionExpanded={isAccordionExpanded}
                title={title}
                handleClose={stop}
                renderContent={renderContent}
              />
            )}
          </Accordion.Item>
        </Accordion>
      ) : (
        <>
          <CloseButton handleClose={stop} />
          <Box padding={7}>{renderContent}</Box>
        </>
      )}
    </Box>
  );
};

TourStepContentComponent.displayName = 'TourStepContent';

/**
 * Memoized TourStepContent component to prevent unnecessary re-renders.
 * Only re-renders when props actually change.
 */
export const TourStepContent = React.memo(TourStepContentComponent);
